import React, { useCallback, useState } from 'react';
import DataSourceConfiguration from './DataSourceConfiguration';

interface ExcelConfigurationProps {
    configuration: {
        // IDataSourceConfiguration Members
        SourceType?: string;
        Configuration?: string;
        FormatConfiguration?: FormatConfiguration;
    };
    onChange: (field: string, value: string | number | boolean | FormatConfiguration) => void;
    disabled?: boolean;
}

interface ExcelConfigurationComponent extends React.FC<ExcelConfigurationProps> {
    configFields: string[];
}

// Format Configuration interfaces
interface CategoryFormatConfiguration {
    Type?: number;
    NameColumnByLevel?: string[];
    CodeColumnByLevel?: string[];
    EmptyFieldsMeanCategory?: string[];
}

interface FormatConfiguration {
    ExternalIdColumn?: string;
    ManufacturerCodeColumn?: string;
    NameColumn?: string;
    VendorColumn?: string;
    PriceColumn?: string;
    StockColumn?: string;
    ExpectedDateColumn?: string;
    CurrencyColumn?: string;
    WarehouseColumn?: string;
    DefaultCurrency?: string;
    DefaultWarehouse?: string;
    EanColumns?: string[];
    EanSeparator?: string;
    HeaderOffset?: number;
    SkipEmptyRows?: boolean;
    CustomDataColumns?: Record<string, string>;
    Category?: CategoryFormatConfiguration;
    SheetName?: string;
    SheetNumber?: number;
    FileType?: number;
}

const fileTypeOptions = [
    { value: 0, label: 'Авто' },
    { value: 1, label: 'XLSX' },
    { value: 2, label: 'XLS' }
];

const categoryTypeOptions = [
    { value: 0, label: 'Нет' },
    { value: 1, label: 'Встроенная' },
    { value: 2, label: 'Иерархическая' }
];

const defaultFormatConfig: FormatConfiguration = {
    DefaultCurrency: 'RUB',
    DefaultWarehouse: 'Default',
    EanSeparator: ',',
    HeaderOffset: 1,
    SkipEmptyRows: true,
    FileType: 0,
    Category: {
        Type: 0
    }
};

const ExcelConfiguration: ExcelConfigurationComponent = ({
    configuration,
    onChange,
    disabled = false
}) => {
    const [formatCfg, setFormatCfg] = useState<FormatConfiguration>(configuration.FormatConfiguration || defaultFormatConfig);
    const [eanColumnsInput, setEanColumnsInput] = useState(() => (configuration.FormatConfiguration?.EanColumns ?? []).join(','));
    const [nameColumnsInput, setNameColumnsInput] = useState(() => (configuration.FormatConfiguration?.Category?.NameColumnByLevel ?? []).join(','));
    const [codeColumnsInput, setCodeColumnsInput] = useState(() => (configuration.FormatConfiguration?.Category?.CodeColumnByLevel ?? []).join(','));
    const [emptyFieldsInput, setEmptyFieldsInput] = useState(() => (configuration.FormatConfiguration?.Category?.EmptyFieldsMeanCategory ?? []).join(','));
    const [customDataKey, setCustomDataKey] = useState('');
    const [customDataValue, setCustomDataValue] = useState('');

    const onFormatChange = useCallback((field: string, value: unknown) => {
        setFormatCfg(prev => {
            const newCfg = { ...prev, [field]: value };
            onChange('FormatConfiguration', newCfg);
            return newCfg;
        });
    }, [onChange]);

    const onCategoryChange = useCallback((field: string, value: unknown) => {
        setFormatCfg(prev => {
            const newCategory = { ...prev.Category, [field]: value };
            const newCfg = { ...prev, Category: newCategory };
            onChange('FormatConfiguration', newCfg);
            return newCfg;
        });
    }, [onChange]);

    const addCustomDataColumn = () => {
        if (!customDataKey || customDataValue === '') return;

        setFormatCfg(prev => {
            const newCustom = { ...(prev.CustomDataColumns ?? {}), [customDataKey]: customDataValue };
            const newCfg = { ...prev, CustomDataColumns: newCustom };
            onChange('FormatConfiguration', newCfg);
            return newCfg;
        });

        setCustomDataKey('');
        setCustomDataValue('');
    };

    const removeCustomDataColumn = (key: string) => {
        setFormatCfg(prev => {
            const newCustom = { ...prev.CustomDataColumns };
            delete newCustom[key];
            const newCfg = { ...prev, CustomDataColumns: newCustom };
            onChange('FormatConfiguration', newCfg);
            return newCfg;
        });
    };

    const parseColumnArray = (input: string): string[] => {
        return input.split(',')
            .map(s => s.trim())
            .filter(s => s !== '');
    };

    const onEanColumnsChange = (value: string) => {
        setEanColumnsInput(value);
        const arr = parseColumnArray(value);
        onFormatChange('EanColumns', arr.length > 0 ? arr : undefined);
    };

    const onNameColumnsChange = (value: string) => {
        setNameColumnsInput(value);
        const arr = parseColumnArray(value);
        onCategoryChange('NameColumnByLevel', arr.length > 0 ? arr : undefined);
    };

    const onCodeColumnsChange = (value: string) => {
        setCodeColumnsInput(value);
        const arr = parseColumnArray(value);
        onCategoryChange('CodeColumnByLevel', arr.length > 0 ? arr : undefined);
    };

    const onEmptyFieldsChange = (value: string) => {
        setEmptyFieldsInput(value);
        const arr = parseColumnArray(value);
        onCategoryChange('EmptyFieldsMeanCategory', arr.length > 0 ? arr : undefined);
    };

    return (
        <React.Fragment>
            <DataSourceConfiguration 
                configuration={{
                    SourceType: configuration.SourceType || '',
                    Configuration: configuration.Configuration || '{}'
                }}
                onChange={onChange}
                disabled={disabled}
            />

            <div className="form-section-title">
                <span className="section-icon">📊</span>
                Конфигурация формата Excel
            </div>

            <div className="form-group-custom">
                <label className="form-label-custom">
                    <span className="label-icon">📄</span>
                    Тип файла
                </label>
                <select
                    className="form-select-custom"
                    value={formatCfg.FileType ?? 0}
                    onChange={(e) => onFormatChange('FileType', parseInt(e.target.value))}
                    disabled={disabled}
                >
                    {fileTypeOptions.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                </select>
            </div>

            <div className="form-group-custom">
                <label className="form-label-custom">
                    <span className="label-icon">📋</span>
                    Имя листа
                </label>
                <input
                    type="text"
                    className="form-input-custom"
                    value={formatCfg.SheetName ?? ''}
                    placeholder="Авто"
                    disabled={disabled}
                    onChange={(e) => onFormatChange('SheetName', e.target.value || undefined)}
                />
            </div>

            <div className="form-group-custom">
                <label className="form-label-custom">
                    <span className="label-icon">🔢</span>
                    Номер листа
                </label>
                <input
                    type="number"
                    className="form-input-custom"
                    value={formatCfg.SheetNumber ?? ''}
                    placeholder="Авто"
                    disabled={disabled}
                    onChange={(e) => onFormatChange('SheetNumber', e.target.value ? parseInt(e.target.value) : undefined)}
                />
            </div>

            <div className="form-group-custom">
                <label className="form-label-custom">
                    <span className="label-icon">📍</span>
                    Смещение заголовка
                </label>
                <input
                    type="number"
                    className="form-input-custom"
                    value={formatCfg.HeaderOffset ?? 1}
                    disabled={disabled}
                    onChange={(e) => onFormatChange('HeaderOffset', parseInt(e.target.value) || 1)}
                />
            </div>

            <div className="form-section-subtitle">Колонки данных</div>

            <div className="form-group-custom">
                <label className="form-label-custom">
                    <span className="label-icon">🆔</span>
                    Колонка кода поставщика
                </label>
                <input
                    className="form-input-custom"
                    value={formatCfg.ExternalIdColumn ?? ''}
                    placeholder="Не задано"
                    disabled={disabled}
                    onChange={(e) => onFormatChange('ExternalIdColumn', e.target.value)}
                />
            </div>

            <div className="form-group-custom">
                <label className="form-label-custom">
                    <span className="label-icon">📝</span>
                    Колонка названия
                </label>
                <input
                    className="form-input-custom"
                    value={formatCfg.NameColumn ?? ''}
                    placeholder="Не задано"
                    disabled={disabled}
                    onChange={(e) => onFormatChange('NameColumn', e.target.value)}
                />
            </div>

            <div className="form-group-custom">
                <label className="form-label-custom">
                    <span className="label-icon">🏭</span>
                    Колонка кода производителя (SKU)
                </label>
                <input
                    className="form-input-custom"
                    value={formatCfg.ManufacturerCodeColumn ?? ''}
                    placeholder="Не задано"
                    disabled={disabled}
                    onChange={(e) => onFormatChange('ManufacturerCodeColumn', e.target.value)}
                />
            </div>            

            <div className="form-group-custom">
                <label className="form-label-custom">
                    <span className="label-icon">🏭</span>
                    Колонка производителя
                </label>
                <input
                    className="form-input-custom"
                    value={formatCfg.VendorColumn ?? ''}
                    placeholder="Не задано"
                    disabled={disabled}
                    onChange={(e) => onFormatChange('VendorColumn', e.target.value)}
                />
            </div>

            <div className="form-group-custom">
                <label className="form-label-custom">
                    <span className="label-icon">💰</span>
                    Колонка цены
                </label>
                <input
                    className="form-input-custom"
                    value={formatCfg.PriceColumn ?? ''}
                    placeholder="Не задано"
                    disabled={disabled}
                    onChange={(e) => onFormatChange('PriceColumn', e.target.value)}
                />
            </div>

            <div className="form-group-custom">
                <label className="form-label-custom">
                    <span className="label-icon">📦</span>
                    Колонка остатка
                </label>
                <input
                    className="form-input-custom"
                    value={formatCfg.StockColumn ?? ''}
                    placeholder="Не задано"
                    disabled={disabled}
                    onChange={(e) => onFormatChange('StockColumn', e.target.value)}
                />
            </div>

            <div className="form-group-custom">
                <label className="form-label-custom">
                    <span className="label-icon">📅</span>
                    Колонка даты ожидания
                </label>
                <input
                    className="form-input-custom"
                    value={formatCfg.ExpectedDateColumn ?? ''}
                    placeholder="Не задано"
                    disabled={disabled}
                    onChange={(e) => onFormatChange('ExpectedDateColumn', e.target.value)}
                />
            </div>

            <div className="form-group-custom">
                <label className="form-label-custom">
                    <span className="label-icon">💱</span>
                    Колонка валюты
                </label>
                <input
                    className="form-input-custom"
                    value={formatCfg.CurrencyColumn ?? ''}
                    placeholder="Не задано"
                    disabled={disabled}
                    onChange={(e) => onFormatChange('CurrencyColumn', e.target.value)}
                />
            </div>

            <div className="form-group-custom">
                <label className="form-label-custom">
                    <span className="label-icon">🏢</span>
                    Колонка склада
                </label>
                <input
                    className="form-input-custom"
                    value={formatCfg.WarehouseColumn ?? ''}
                    placeholder="Не задано"
                    disabled={disabled}
                    onChange={(e) => onFormatChange('WarehouseColumn', e.target.value)}
                />
            </div>

            <div className="form-group-custom">
                <label className="form-label-custom">
                    <span className="label-icon">🏷️</span>
                    Колонки EAN (через запятую)
                </label>
                <input
                    type="text"
                    className="form-input-custom"
                    value={eanColumnsInput}
                    placeholder="A,B,C"
                    disabled={disabled}
                    onChange={(e) => onEanColumnsChange(e.target.value)}
                />
            </div>

            <div className="form-section-subtitle">Значения по умолчанию</div>

            <div className="form-group-custom">
                <label className="form-label-custom">
                    <span className="label-icon">💱</span>
                    Валюта по умолчанию
                </label>
                <input
                    type="text"
                    className="form-input-custom"
                    value={formatCfg.DefaultCurrency ?? 'RUB'}
                    disabled={disabled}
                    onChange={(e) => onFormatChange('DefaultCurrency', e.target.value)}
                />
            </div>

            <div className="form-group-custom">
                <label className="form-label-custom">
                    <span className="label-icon">🏢</span>
                    Склад по умолчанию
                </label>
                <input
                    type="text"
                    className="form-input-custom"
                    value={formatCfg.DefaultWarehouse ?? 'Default'}
                    disabled={disabled}
                    onChange={(e) => onFormatChange('DefaultWarehouse', e.target.value)}
                />
            </div>

            <div className="form-group-custom">
                <label className="form-label-custom">
                    <span className="label-icon">🔀</span>
                    Разделитель EAN
                </label>
                <input
                    type="text"
                    className="form-input-custom"
                    value={formatCfg.EanSeparator ?? ','}
                    disabled={disabled}
                    onChange={(e) => onFormatChange('EanSeparator', e.target.value)}
                />
            </div>

            <div className="form-group-custom">
                <label className="form-label-custom">
                    <span className="label-icon">🔄</span>
                    Пропускать пустые строки
                </label>
                <label className="form-check-custom">
                    <input
                        type="checkbox"
                        className="form-check-input-custom"
                        checked={formatCfg.SkipEmptyRows ?? true}
                        disabled={disabled}
                        onChange={(e) => onFormatChange('SkipEmptyRows', e.target.checked)}
                    />
                    <span className="form-check-label-custom">Включить</span>
                </label>
            </div>

            <div className="form-section-subtitle">Категории</div>

            <div className="form-group-custom">
                <label className="form-label-custom">
                    <span className="label-icon">📂</span>
                    Тип категории
                </label>
                <select
                    className="form-select-custom"
                    value={formatCfg.Category?.Type ?? 0}
                    onChange={(e) => onCategoryChange('Type', parseInt(e.target.value))}
                    disabled={disabled}
                >
                    {categoryTypeOptions.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                </select>
            </div>

            {formatCfg.Category?.Type !== 0 && (
                <>
                    <div className="form-group-custom">
                        <label className="form-label-custom">
                            <span className="label-icon">📋</span>
                            Колонки названий по уровням
                        </label>
                        <input
                            type="text"
                            className="form-input-custom"
                            value={nameColumnsInput}
                            placeholder="A,B,C"
                            disabled={disabled}
                            onChange={(e) => onNameColumnsChange(e.target.value)}
                        />
                    </div>

                    <div className="form-group-custom">
                        <label className="form-label-custom">
                            <span className="label-icon">🔢</span>
                            Колонки кодов по уровням
                        </label>
                        <input
                            type="text"
                            className="form-input-custom"
                            value={codeColumnsInput}
                            placeholder="A,B,C"
                            disabled={disabled}
                            onChange={(e) => onCodeColumnsChange(e.target.value)}
                        />
                    </div>

                    <div className="form-group-custom">
                        <label className="form-label-custom">
                            <span className="label-icon">⚠️</span>
                            Пустые поля = категория
                        </label>
                        <input
                            type="text"
                            className="form-input-custom"
                            value={emptyFieldsInput}
                            placeholder="A,B"
                            disabled={disabled}
                            onChange={(e) => onEmptyFieldsChange(e.target.value)}
                        />
                    </div>
                </>
            )}

            <div className="form-section-subtitle">Пользовательские колонки</div>

            <div className="form-group-custom">
                <label className="form-label-custom">
                    <span className="label-icon">🔑</span>
                    Ключ
                </label>
                <input
                    type="text"
                    className="form-input-custom"
                    value={customDataKey}
                    placeholder="Имя поля"
                    disabled={disabled}
                    onChange={(e) => setCustomDataKey(e.target.value)}
                />
            </div>

            <div className="form-group-custom">
                <label className="form-label-custom">
                    <span className="label-icon">#️⃣</span>
                    Номер колонки
                </label>
                <div className="input-with-button">
                    <input
                        type="number"
                        className="form-input-custom"
                        value={customDataValue}
                        placeholder="1"
                        disabled={disabled}
                        onChange={(e) => setCustomDataValue(e.target.value)}
                    />
                    <button
                        type="button"
                        className="btn-icon-custom"
                        onClick={addCustomDataColumn}
                        disabled={disabled || !customDataKey || !customDataValue}
                    >
                        ➕
                    </button>
                </div>
            </div>

            {formatCfg.CustomDataColumns && Object.keys(formatCfg.CustomDataColumns).length > 0 && (
                <div className="custom-data-list">
                    {Object.entries(formatCfg.CustomDataColumns).map(([key, value]) => (
                        <div key={key} className="custom-data-item">
                            <span className="custom-data-key">{key}</span>
                            <span className="custom-data-value">{value}</span>
                            <button
                                type="button"
                                className="btn-icon-remove"
                                onClick={() => removeCustomDataColumn(key)}
                                disabled={disabled}
                            >
                                ✕
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </React.Fragment>
    );
};

ExcelConfiguration.configFields = [
    'SourceType',
    'Configuration',
    'FormatConfiguration'
];

export default ExcelConfiguration;
